/*
 * Entrar con Google. Google confirma quién eres (con su verificación en 2 pasos) y la
 * API decide si puedes entrar: solo los emails de su lista cerrada, cada uno ya
 * atado a una persona del equipo, así que "quién eres" no se elige aquí.
 *
 * En el demo local no hay Google: la API avisa (`demo`) y se entra con un botón por
 * persona.
 */

import { useEffect, useRef, useState } from "react";

import { api, sesion, type SesionApi } from "../api/cliente";
import { cargarGoogle } from "../api/google";
import { EQUIPO } from "../api/tipos";

type ConfigAcceso = { google_client_id: string | null; demo: boolean };

export function Acceso({ onEntrar }: { onEntrar: () => void }) {
  const [config, setConfig] = useState<ConfigAcceso | null>(null);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);
  const boton = useRef<HTMLDivElement>(null);

  const entrar = async (pedido: () => Promise<SesionApi>) => {
    setCargando(true); setError("");
    try {
      sesion.entrar(await pedido());
      onEntrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo entrar.");
    } finally { setCargando(false); }
  };

  useEffect(() => {
    api<ConfigAcceso>("/api/acceso/config").then(setConfig).catch(() => setError("No se pudo contactar la API."));
  }, []);

  useEffect(() => {
    const clientId = config?.google_client_id;
    if (!clientId) return;
    let vivo = true;
    cargarGoogle().then((g) => {
      if (!vivo || !boton.current) return;
      g.accounts.id.initialize({
        client_id: clientId,
        ux_mode: "popup",
        callback: (r) => void entrar(() => api<SesionApi>("/api/acceso/google", { body: { credencial: r.credential } })),
      });
      g.accounts.id.renderButton(boton.current, { theme: "outline", size: "large", text: "signin_with", shape: "pill", locale: "es", width: 320 });
    }).catch(() => setError("No se pudo cargar el inicio de sesión de Google. Revisa tu conexión."));
    return () => { vivo = false; };
  }, [config]);

  const sinConfigurar = config !== null && !config.google_client_id && !config.demo;

  return (
    <div style={{ minHeight: "100%", display: "grid", placeItems: "center", background: "var(--navy)", padding: 24 }}>
      <div style={{ width: 380, maxWidth: "100%", background: "var(--card)", borderRadius: "var(--r-container)", padding: "28px 30px", display: "flex", flexDirection: "column", gap: 16 }}>
        <img src="/assets/nexlum_icon.png" alt="Nexlum" style={{ height: 26, width: "auto", alignSelf: "flex-start" }} />
        <div>
          <h1 className="titulo" style={{ fontSize: 20 }}>Nexlum OS</h1>
          <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 2 }}>Entra con tu cuenta de Google. Solo las cuentas del equipo tienen acceso.</p>
        </div>
        {config?.google_client_id && <div ref={boton} style={{ minHeight: 44, opacity: cargando ? 0.5 : 1 }} />}
        {config?.demo && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>Demo local, sin Google:</span>
            {EQUIPO.map((p) => (
              <button key={p.id} className="btn btn-secundario" disabled={cargando} style={{ justifyContent: "center" }}
                onClick={() => void entrar(() => api<SesionApi>("/api/acceso/demo", { body: { persona_id: p.id } }))}>
                Entrar como {p.nombre}
              </button>
            ))}
          </div>
        )}
        {sinConfigurar && <p style={{ fontSize: 13, color: "var(--risk)" }}>El servidor no tiene GOOGLE_CLIENT_ID configurado.</p>}
        {error && <p role="alert" style={{ fontSize: 13, color: "var(--risk)" }}>{error}</p>}
      </div>
    </div>
  );
}
